import { useEffect, useMemo, useState } from 'react';

import { ApiClient, formatDateTime, useAsync, useMutation } from '@clientma/core';
import { Alert, Badge, Button, Card, Field, Input, Select, Skeleton, Stat, Textarea, useToast } from '@clientma/ui';

import { providerApi, type ProviderAiInput, type ProviderAiView } from '../api';

/**
 * The model ClientMa itself answers with.
 *
 * One key, shared by every customer on prepaid credits, which is why this screen belongs to an
 * operator who can see that blast radius rather than to any single customer — and why it says
 * how many tenants depend on it before it lets anybody change it.
 */
export default function AiKeys({ api }: { api: ApiClient }) {
  const provider = useMemo(() => providerApi(api), [api]);
  const toast = useToast();

  const current = useAsync((signal) => provider.aiProfile(signal), [provider]);
  const plans = useAsync((signal) => provider.plans(signal), [provider]);

  const [form, setForm] = useState<ProviderAiInput | null>(null);
  const [apiKey, setApiKey] = useState('');

  useEffect(() => {
    if (!current.data) return;

    setForm(toForm(current.data));
    // Never pre-filled, because it never comes back from the server. An empty box that saves
    // without clearing the stored key is the honest version of this.
    setApiKey('');
  }, [current.data]);

  const save = useMutation((input: ProviderAiInput) => provider.saveAiProfile(input), {
    onSuccess: (saved) => {
      if (saved.isActive) {
        toast.success('Saved', 'This key now answers for everyone on credits.');
      } else {
        // The commonest way to be confused here: the key is saved, nothing uses it, and the
        // check then reports the switch rather than the key.
        toast.success(
          'Saved, but not in use',
          'Tick “Use this instead of the key in configuration” to make it answer.',
        );
      }
      current.reload();
    },
    onError: (error) => toast.error('Could not save', error.message),
  });

  const verify = useMutation(() => provider.verifyAiProfile(), {
    onSuccess: (result) => {
      if (result.verifiedAtUtc) toast.success('The model answered', `${result.family} · ${result.model}`);
      else toast.error('The model did not answer', result.verificationError ?? undefined);
      current.reload();
    },
    onError: (error) => toast.error('Could not check it', error.message),
  });

  const clearKey = useMutation(() => provider.clearAiKey(), {
    onSuccess: () => {
      toast.success('The key was removed', 'Falling back to configuration.');
      current.reload();
    },
    onError: (error) => toast.error('Could not remove it', error.message),
  });

  if (current.isLoading || !form) return <Skeleton height={320} />;

  if (current.error) {
    return (
      <Alert tone="danger" title="The AI profile could not be read">
        {current.error.message}
      </Alert>
    );
  }

  const view = current.data!;

  return (
    <div className="cma-stack">
      <div className="provider-actionrow">
        <div>
          <h2 className="provider-h2">The model ClientMa answers with</h2>
          <p className="provider-muted">
            Used by every customer on prepaid credits. A customer who brings their own provider
            account is unaffected by anything on this page.
          </p>
        </div>

        {view.isActive ? (
          <Badge tone="success" dot>
            In use
          </Badge>
        ) : (
          <Badge tone="neutral" dot>
            Standing by
          </Badge>
        )}
      </div>

      <div className="cma-stat-row">
        <Stat
          label="Customers on credits"
          value={String(view.tenantsOnCredits)}
          meta="depend on this key"
        />
        <Stat
          label="Key on file"
          value={view.hasApiKey ? (view.keyHint ?? 'yes') : 'none'}
          meta={view.hasApiKey ? 'stored encrypted' : undefined}
        />
        <Stat
          label="Last checked"
          value={view.verifiedAtUtc ? formatDateTime(view.verifiedAtUtc, 'en-SG') : 'never'}
          meta={view.verificationError ? 'it refused' : undefined}
        />
      </div>

      {/*
        The two states worth saying out loud. An operator standing this row down needs to know
        whether they are falling back onto a working arrangement or onto nothing at all.
      */}
      {!view.isActive && view.hasConfiguredFallback && (
        <Alert tone="info" title="Configuration is answering">
          This profile is standing by, so the key in configuration is in use. Switch this on to
          take over — you can switch it off again to fall back.
        </Alert>
      )}

      {!view.isActive && !view.hasConfiguredFallback && view.tenantsOnCredits > 0 && (
        <Alert tone="warning" title="Nothing is answering">
          {view.tenantsOnCredits} customer{view.tenantsOnCredits === 1 ? '' : 's'} on prepaid
          credits, no key in configuration, and this profile is switched off. Their assistants
          will say so politely and nothing else will break.
        </Alert>
      )}

      {view.verificationError && (
        <Alert tone="danger" title="The last check failed">
          {view.verificationError}
        </Alert>
      )}


      {/*
        Where the allowances live.

        This page is the key; the allowances are somewhere else, and an operator looking for
        "how many credits does a customer get" comes here first and finds nothing about it. The
        two places are named, and what each plan currently gives is shown, because the answer to
        the question is usually just that number.
      */}
      <Card title="How many credits a customer gets">
        <p className="provider-muted">
          The monthly allowance comes from the customer&rsquo;s plan. One customer can be given a
          different figure, and that wins.
        </p>

        {plans.isLoading ? (
          <Skeleton height={90} />
        ) : (
          <div className="provider-table-wrap">
            <table className="provider-table">
              <thead>
                <tr>
                  <th>Plan</th>
                  <th>Tier</th>
                  <th className="provider-num">Credits a month</th>
                </tr>
              </thead>
              <tbody>
                {(plans.data ?? [])
                  .filter((plan) => plan.isActive)
                  .map((plan) => (
                    <tr key={plan.id}>
                      <td>{plan.name}</td>
                      <td className="provider-muted">{plan.tier}</td>
                      <td className="provider-num">
                        {plan.aiCreditsPerMonth
                          ? plan.aiCreditsPerMonth.toLocaleString()
                          : 'none'}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}

        <ul className="provider-muted" style={{ margin: 0, paddingLeft: '1.1rem' }}>
          <li>
            To change what a plan gives everybody on it: <strong>Plans</strong> &rarr; open the
            plan &rarr; <strong>Entitlements</strong> &rarr; the AI assistant limit.
          </li>
          <li>
            To give one customer a different figure, or a one-off top-up:{' '}
            <strong>Customers</strong> &rarr; open them &rarr; <strong>AI credits</strong>.
          </li>
        </ul>
      </Card>

      <Card title="Credentials">
        <div className="cma-stack">
          <Field label="Family" hint="The wire format, not the brand. Only what this build can talk to.">
            {(id) => (
              <Select
                id={id}
                value={form.family}
                onChange={(event) => setForm({ ...form, family: event.target.value })}
              >
                {view.supportedProviders.map((family) => (
                  <option key={family} value={family}>
                    {family === 'anthropic' ? 'Anthropic' : 'OpenAI-compatible'}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field label="Model" hint="Exactly as the provider names it, for example claude-sonnet-5.">
            {(id) => (
              <Input
                id={id}
                value={form.model}
                onChange={(event) => setForm({ ...form, model: event.target.value })}
              />
            )}
          </Field>

          <Field label="Endpoint" hint="Empty uses the provider's own. Set it for a regional gateway.">
            {(id) => (
              <Input
                id={id}
                value={form.baseUrl ?? ''}
                placeholder="https://…"
                onChange={(event) => setForm({ ...form, baseUrl: event.target.value })}
              />
            )}
          </Field>

          <Field
            label="API key"
            hint={
              view.hasApiKey
                ? `A key ending ${view.keyHint ?? '••••'} is on file. Leave empty to keep it, or paste a new one to rotate.`
                : 'Stored encrypted and never shown again.'
            }
          >
            {(id) => (
              <Input
                id={id}
                type="password"
                value={apiKey}
                autoComplete="off"
                placeholder={view.hasApiKey ? '••••••••••••' : 'sk-…'}
                onChange={(event) => setApiKey(event.target.value)}
              />
            )}
          </Field>

          <label className="cma-check">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(event) => setForm({ ...form, isActive: event.target.checked })}
            />
            <span>
              Use this instead of the key in configuration
              {view.tenantsOnCredits > 0 && ` — ${view.tenantsOnCredits} customer${view.tenantsOnCredits === 1 ? '' : 's'} affected`}
            </span>
          </label>

          <Field label="Notes" hint="For whoever rotates this next.">
            {(id) => (
              <Textarea
                id={id}
                rows={2}
                value={form.notes ?? ''}
                onChange={(event) => setForm({ ...form, notes: event.target.value })}
              />
            )}
          </Field>

          {save.error && <Alert tone="danger">{save.error.message}</Alert>}

          <div className="cma-row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <Button
              disabled={save.isRunning || form.model.trim().length === 0}
              onClick={() => void save.run({ ...form, apiKey: apiKey.trim() || null })}
            >
              {save.isRunning ? 'Saving…' : apiKey.trim() ? 'Save and rotate the key' : 'Save'}
            </Button>

            <Button
              variant="secondary"
              disabled={verify.isRunning || (!view.hasApiKey && !view.hasConfiguredFallback)}
              onClick={() => void verify.run(undefined as never)}
              title={
                !view.hasApiKey && !view.hasConfiguredFallback
                  ? 'Save a key first — there is nothing to check.'
                  : 'Asks the service a trivial question and records whether it answered.'
              }
            >
              {verify.isRunning ? 'Checking…' : 'Check it works'}
            </Button>

            {view.hasApiKey && (
              <Button
                variant="ghost"
                disabled={clearKey.isRunning}
                onClick={() => void clearKey.run(undefined as never)}
              >
                Remove the key
              </Button>
            )}
          </div>

          <p className="provider-muted">
            {view.updatedByName
              ? `Last changed by ${view.updatedByName}${view.updatedAtUtc ? ` on ${formatDateTime(view.updatedAtUtc, 'en-SG')}` : ''}.`
              : 'Never changed from this console.'}
          </p>
        </div>
      </Card>
    </div>
  );
}

function toForm(view: ProviderAiView): ProviderAiInput {
  return {
    family: view.family,
    model: view.model ?? '',
    baseUrl: view.baseUrl ?? null,
    isActive: view.isActive,
    notes: view.notes ?? null,
  };
}
